import React, {
	createContext,
	ReactNode,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useWallet } from '@aptos-labs/wallet-adapter-react';
import { hasToken } from 'lib/token';
import { createOrGetUser } from 'api/client';
import { AGENTS_LIST } from 'data/Agents';

interface UserContextType {
	userId: string | null;
	walletAddress: string | null;
	baseUrl: string;
	isLoading: boolean;
	isLoggedIn: boolean;
	setUserId: (id: string | null) => void;
	setBaseUrl: (url: string) => void;
	logout: () => void;
}

const UserContext = createContext<UserContextType | null>(null);

export function UserProvider({ children }: { children: ReactNode }) {
	const {
		connected,
		account,
		connect,
		disconnect,
		isLoading: isWalletLoading,
		wallets,
	} = useWallet();
	const didAttemptAutoReconnect = useRef(false);
	const isHyperionOnly = useMemo(() => {
		if (typeof window === 'undefined') return false;

		const params = new URLSearchParams(window.location.search);
		const vaultParam =
			params.get('vault') || params.get('valut') || params.get('Vault');

		return (vaultParam || '').toLowerCase() === 'hyperion';
	}, []);
	const [userId, setUserId] = useState<string | null>(null);
	const [baseUrl, setBaseUrl] = useState<string>(
		(isHyperionOnly
			? AGENTS_LIST.find((f) => f.details)?.baseURL
			: AGENTS_LIST[0]?.baseURL) || '',
	);
	const [isFetchingUserId, setIsFetchingUserId] = useState(false);

	useEffect(() => {
		if (didAttemptAutoReconnect.current) return;
		if (connected) {
			didAttemptAutoReconnect.current = true;
			return;
		}
		if (isWalletLoading) return;

		const walletName =
			typeof window !== 'undefined'
				? localStorage.getItem('AptosWalletName')
				: null;
		if (!walletName) {
			didAttemptAutoReconnect.current = true;
			return;
		}

		const hasWallet = wallets?.some((wallet) => wallet.name === walletName);
		if (!hasWallet) {
			didAttemptAutoReconnect.current = true;
			return;
		}

		didAttemptAutoReconnect.current = true;
		connect(walletName);
	}, [connected, connect, isWalletLoading, wallets]);

	useEffect(() => {
		const fetchUserIdOnReconnect = async () => {
			const walletAddress = account?.address
				? String(account.address)
				: null;
			const tokenExists = hasToken();

			if (
				tokenExists &&
				connected &&
				walletAddress &&
				!userId &&
				!isFetchingUserId
			) {
				setIsFetchingUserId(true);
				try {
					const { id } = await createOrGetUser(
						walletAddress,
						undefined,
						baseUrl,
					);
					setUserId(id);
				} catch (error) {
					/* console.error(
						'❌ [UserContext] Failed to fetch userId on reconnect:',
						error,
					);*/
				} finally {
					setIsFetchingUserId(false);
				}
			}
		};

		fetchUserIdOnReconnect();
	}, [connected, account, userId, isFetchingUserId, baseUrl]);

	useEffect(() => {
		if (!baseUrl || !connected || !account?.address) {
			return;
		}

		const walletAddress = String(account.address);
		const tokenExists = hasToken();

		if (!tokenExists) return;

		let cancelled = false;

		const syncUserForBaseUrl = async () => {
			setIsFetchingUserId(true);
			try {
				const { id } = await createOrGetUser(
					walletAddress,
					undefined,
					baseUrl,
				);
				if (!cancelled) {
					setUserId(id);
				}
			} catch (error) {
				if (!cancelled) {
					/* console.error(
						'❌ [UserContext] Failed to sync user on baseUrl change:',
						error,
					);*/
				}
			} finally {
				if (!cancelled) {
					setIsFetchingUserId(false);
				}
			}
		};

		syncUserForBaseUrl();

		return () => {
			cancelled = true;
		};
	}, [account, baseUrl, connected]);

	useEffect(() => {
		if (!connected) {
			setUserId(null);
		}
	}, [connected]);

	const tokenExists = hasToken();

	const isLoggedIn = tokenExists && connected && !!account;

	const isLoading = isWalletLoading || isFetchingUserId;

	const logout = () => {
		setUserId(null);
		disconnect();
	};

	return (
		<UserContext.Provider
			value={{
				walletAddress: account?.address
					? String(account.address)
					: null,
				userId: userId && !userId.startsWith('0x') ? userId : null,
				baseUrl,
				isLoading,
				isLoggedIn,
				setUserId,
				setBaseUrl,
				logout,
			}}
		>
			{children}
		</UserContext.Provider>
	);
}

export function useUserContext() {
	const context = useContext(UserContext);
	if (!context) {
		throw new Error('useUserContext must be used within UserProvider');
	}
	return context;
}
